import { Injectable } from '@angular/core';
import { Order, QueryEntity } from '@datorama/akita';
import { compareEntities, compareEntity } from '@lycoris-nubila/ngx-lycoris';
import { isNil, reject, uniqBy } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { NotificationType } from 'application/notification/model/notification.type';
import { NotificationRepositoryPort } from 'application/notification/port/notification-repository.port';
import { INotification, Notification } from 'domain/notification/notification';

import { CacheObservable } from '../../app-decorators';
import { NotificationState, NotificationStore } from './notification.store';

@Injectable()
export class NotificationRepositoryAdapter
	extends QueryEntity<NotificationState>
	implements NotificationRepositoryPort
{
	constructor(protected store: NotificationStore) {
		super(store);
	}

	public deleteNotification(id: string): void {
		this.store.remove(id);
	}

	public deleteNotifications(ids: string[]): void {
		this.store.remove(ids);
	}

	@CacheObservable()
	public observeNotification(notificationId: string): Observable<INotification> {
		return this.selectEntity(notificationId).pipe(
			map((document) => this.store.asEntity(document)),
			distinctUntilChanged(compareEntity),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeNotifications(): Observable<INotification[]> {
		return this.selectAll({
			sortByOrder: Order.DESC,
			sortBy: 'creation',
		}).pipe(
			map((documents) =>
				documents.map((document) => this.store.asEntity(document))
			),
			distinctUntilChanged(compareEntities),
			map((documents) =>
				documents.filter(
					(doc) =>
						(doc.type != NotificationType.BASIC_ASSESSMENT_DOCUMENT_GENERATED &&
							doc.type != NotificationType.DETAILED_ASSESSMENT_DOCUMENT_GENERATED) ||
						(doc.type != NotificationType.BASIC_ASSESSMENT_DOCUMENT_GENERATED &&
							doc.type == NotificationType.DETAILED_ASSESSMENT_DOCUMENT_GENERATED) ||
						(documents.every(
							(d) => d.type != NotificationType.DETAILED_ASSESSMENT_DOCUMENT_GENERATED
						) &&
							doc.type == NotificationType.BASIC_ASSESSMENT_DOCUMENT_GENERATED)
				)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public saveNotifications(entities: Notification[]): void {
		this.store.upsertMany(
			uniqBy(
				reject(entities, isNil).map((e) => this.store.asDocument(e)),
				'id'
			)
		);
	}
}
